import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { inject } from '@app/providers/QAU.ContextProvider';
import ApplyButton from '../apply-button/ApplyButton';
import ShapeSelect from '../shape-select/ShapeSelect';
import BackgroundTool from '../background-tool/BackgroundTool';
import ResizeTool from '../resize-tool/ResizeTool';
import Button from '@components/buttons/Button';
import LocalizedText from '@LocalizedText';
import { fetchShapes, setResizeParams, setBgParams } from '@app/actions/customizeActions';
import { setFooterConfig } from '@app/actions/footerActions';
import { isCreatedFromDielineShape } from '@app/utils/hints';
import {
  isMobileMode,
  isCustomShape,
  getOriginalSourceValue,
  isStickerFlow,
  isDisableShapeChange,
  convertToFlatShapeList,
  isCornerTypeOptionsEnabled,
} from '@app/utils/utils';
import { toTwips } from '@app/utils/measurement';
import { getNonCustomShapeName } from '@app/utils/hints';

const SizeToolGroup = ({
  isSticker,
  disableShapeChange,
  resizeParams,
  fetchShapes,
  setBgParams,
  setResizeParams,
  setFooterConfig,
  flow,
  context,
  matrix,
  isMobileView,
  bgParams,
  artworkImage,
  measurementLocale: locale = 'inches',
  cornerTypeOptionsEnabled,
}) => {
  const {
    isShapeChanged,
    isCornerTypeChanged,
    isSizeChanged,
    hasValidationErrors,
    selectedSizes,
    selectedShape,
    selectedCornerType,
    currentCornerType,
    shapeList,
    flatShapeList,
    createdFromDielineShape,
  } = resizeParams;
  const {
    bgVisibilityChanged,
    contourCutChanged,
  } = bgParams;
  const {
    ctx: { shape: currentShape },
  } = matrix;
  const currentFlow = isSticker ? 'sticker' : 'label';
  const isApplyEnabled =
    (isShapeChanged || isCornerTypeChanged || isSizeChanged || bgVisibilityChanged || contourCutChanged) && !hasValidationErrors;
  const isNextEnabled = !isApplyEnabled && !hasValidationErrors;
  const isCustomShapeSelected = isCustomShape(selectedShape);
  const isCustomShapeSelectedNotApplied = isCustomShapeSelected && currentShape !== selectedShape;
  const isNonCustomProduct = !isSticker && (!shapeList || !flatShapeList.find(({ shape }) => shape === selectedShape));
  const nonCustomShapeName = isNonCustomProduct ? getNonCustomShapeName(context) : '';
  const isResizeToolDisabled = isCustomShapeSelectedNotApplied || isNonCustomProduct;

  const fetchShapesData = useCallback(() => {
    const productProfile = 'WePrint';
    const {
      product: { id: productId },
    } = flow.projectData;

    fetchShapes({ productProfile, productId }).then(shapeList => {
      setResizeParams({ shapeList, flatShapeList: convertToFlatShapeList(shapeList) });
    });
  }, [fetchShapes]);

  useEffect(() => {
    !shapeList && fetchShapesData();
  }, [shapeList]);

  useEffect(() => {
    if (!selectedShape) {
      setResizeParams({
        selectedShape: currentShape,
      });
    }
  }, [selectedShape]);

  useEffect(() => {
    if (!selectedCornerType) {
      setResizeParams({
        selectedCornerType: currentCornerType,
      });
    }
  }, [selectedCornerType]);

  const onApplyChanges = () => {
    let { width, height } = selectedSizes;

    if (isCustomShapeSelectedNotApplied) {
      width = 0;
      height = 0;
    }

    setResizeParams({
      selectedSizes: {
        width,
        height,
      },
      shouldApply: true,
    });
  };

  const onResetChanges = () => {
    const {
      ctx: { width, height, shape },
    } = matrix;

    setResizeParams({
      selectedSizes: {
        width: toTwips(width, locale),
        height: toTwips(height, locale),
      },
      selectedShape: shape,
      selectedCornerType: currentCornerType,
      isShapeChanged: false,
      isCornerTypeChanged: false,
      isSizeChanged: false,
      shouldApply: false,
      shouldReset: true,
    });
    setBgParams({
      bgVisibilityChanged: false,
      contourCutChanged: false,
    });
  };

  useEffect(() => {
    const messageLocKey =
      !!getOriginalSourceValue(flow) && bgVisibilityChanged
        ? 'qau-keep-remove-bg-white-adjustments-message-text'
        : 'qau-click-apply-footer-note-message-text';
    setFooterConfig({
      nextDisabled: !isNextEnabled,
      noteMessageLocKey: !isMobileView && isApplyEnabled ? messageLocKey : '',
    });
  }, [setFooterConfig, resizeParams, bgVisibilityChanged, contourCutChanged, artworkImage]);

  return (
    <>
      {!isMobileView && <ShapeSelect
        disabled={disableShapeChange}
        currentFlow={currentFlow}
        cornerTypeOptionsEnabled={cornerTypeOptionsEnabled}
        placeholder={nonCustomShapeName ? `qau-dropdown-option-${nonCustomShapeName}` : ''}
      />}
      {!isMobileView && !createdFromDielineShape && <BackgroundTool
        disabled={isResizeToolDisabled}
        createdFromDielineShape={createdFromDielineShape}
      />}
      <ResizeTool
        currentFlow={currentFlow}
        disabled={isResizeToolDisabled}
        clear={isCustomShapeSelectedNotApplied}
      />
      <div className="resize-tool-buttons-container">
        <ApplyButton
            onClick={onApplyChanges}
            disabled={!isApplyEnabled}
        />
        {isApplyEnabled && (
          <Button className="reset-size-button" onClick={onResetChanges}>
            <LocalizedText id="qau-toolgroup-reset-btn" tagName="span" />
          </Button>
        )}
      </div>
    </>
  );
};

SizeToolGroup.propTypes = {
  resizeParams: PropTypes.object.isRequired,
  fetchShapes: PropTypes.func.isRequired,
  setBgParams: PropTypes.func.isRequired,
  setResizeParams: PropTypes.func.isRequired,
  setFooterConfig: PropTypes.func.isRequired,
  flow: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  matrix: PropTypes.object.isRequired,
  isMobileView: PropTypes.bool.isRequired,
  isSticker: PropTypes.bool.isRequired,
  aspectRatioLocked: PropTypes.bool.isRequired,
  artworkImage: PropTypes.object,
  cornerTypeOptionsEnabled: PropTypes.bool.isRequired,
};

const mapStateToProps = ({
  customize: {
    customizeData: {
      bgParams,
      resizeParams,
      scaleParams: { aspectRatioLocked },
      measurementLocale,
    },
  },
  matrix,
  artwork: {image: artworkImage },
}, { flowConfig, context }) => ({
  resizeParams,
  bgParams,
  matrix,
  isMobileView: isMobileMode(matrix.ctx),
  aspectRatioLocked,
  measurementLocale,
  isSticker: isStickerFlow(flowConfig),
  disableShapeChange: isDisableShapeChange(flowConfig) || isCreatedFromDielineShape(context),
  artworkImage,
  cornerTypeOptionsEnabled: isCornerTypeOptionsEnabled(flowConfig),
});

const mapDispatchToProps = {
  fetchShapes,
  setBgParams,
  setResizeParams,
  setFooterConfig,
};

export default inject('flowConfig', 'flow', 'context')(connect(mapStateToProps, mapDispatchToProps)(SizeToolGroup));
